/**
=========================================================
* INDataTable — standard app MaterialReactTable wrapper
=========================================================
* Same layout as the Users screen: CustomHeightTable theme + Card + compact MRT
* with shared header/body borders. Pass any extra MRT props; they merge with or
* override defaults (initialState and mui* row/cell props are merged safely).
=========================================================
*/
/* eslint-disable react/forbid-prop-types */
import PropTypes from "prop-types";
import { Fragment, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Card, ThemeProvider } from "@mui/material";
import MDTypography from "components/MDTypography";
import CustomHeightTable from "assets/theme/components/table/CustomHeightTable";

const defaultInitialState = {
  density: "compact",
  showGlobalFilter: false,
  columnVisibility: {},
  pagination: { pageSize: 10, pageIndex: 0 },
};

function mergeSxObjects(a, b) {
  if (!b) return a;
  if (!a) return b;
  if (typeof a === "function" || typeof b === "function") {
    return (theme) => ({
      ...(typeof a === "function" ? a(theme) : a),
      ...(typeof b === "function" ? b(theme) : b),
    });
  }
  return { ...a, ...b };
}

function mergeMuiCellProps(base, override) {
  if (!override) return base;
  if (typeof override === "function") {
    return (arg) => {
      const o = override(arg) || {};
      return {
        ...base,
        ...o,
        sx: mergeSxObjects(base.sx, o.sx),
      };
    };
  }
  return {
    ...base,
    ...override,
    sx: mergeSxObjects(base.sx, override.sx),
  };
}

function mergeMuiRowProps(base, override) {
  if (!override) return base;
  if (typeof override === "function") {
    return (ctx) => {
      const o = override(ctx) || {};
      return {
        ...base,
        ...o,
        sx: mergeSxObjects(base.sx, o.sx),
      };
    };
  }
  return {
    ...base,
    ...override,
    sx: mergeSxObjects(base.sx, override.sx),
  };
}

/** Default header underline + weight (theme supplies F7F8FF / #402899). */
const defaultHeadCell = {
  sx: {
    borderBottom: "1.5px solid #8090AA",
    fontWeight: 600,
  },
};

const defaultBodyCell = {
  sx: {
    borderBottom: "1px solid #B5BFD0",
  },
};

const defaultBodyRow = {};

/**
 * @param {object} [tableTheme] — Optional MUI theme override (e.g. SettingCustomTable, NoborderTable).
 * @param {boolean} [disableCard] — If true, only the themed table is rendered (e.g. dialogs, nested settings).
 * @param {string} [sectionTitle] — Optional label above the card (e.g. "Users").
 * @param {string} [title] — Alias for sectionTitle (legacy).
 */
function INDataTable({
  sectionTitle,
  title,
  columns,
  data,
  cardSx,
  initialState,
  tableTheme,
  disableCard,
  ...rest
}) {
  const heading = sectionTitle ?? title;
  const resolvedTheme = tableTheme ?? CustomHeightTable;

  const initialFromRest = rest.initialState;
  const mrtRest = { ...rest };
  delete mrtRest.initialState;

  const headOverride = mrtRest.muiTableHeadCellProps;
  const bodyCellOverride = mrtRest.muiTableBodyCellProps;
  const bodyRowOverride = mrtRest.muiTableBodyRowProps;
  delete mrtRest.muiTableHeadCellProps;
  delete mrtRest.muiTableBodyCellProps;
  delete mrtRest.muiTableBodyRowProps;

  const mergedBodyRowProps = useMemo(
    () => mergeMuiRowProps(defaultBodyRow, bodyRowOverride),
    [bodyRowOverride]
  );
  const mergedBodyCellProps = useMemo(
    () => mergeMuiCellProps(defaultBodyCell, bodyCellOverride),
    [bodyCellOverride]
  );
  const mergedHeadCellProps = useMemo(
    () => mergeMuiCellProps(defaultHeadCell, headOverride),
    [headOverride]
  );

  const mergedInitialState = useMemo(
    () => ({
      ...defaultInitialState,
      ...initialFromRest,
      ...initialState,
      pagination: {
        ...defaultInitialState.pagination,
        ...initialFromRest?.pagination,
        ...initialState?.pagination,
      },
    }),
    [initialFromRest, initialState]
  );

  const table = (
    <MaterialReactTable
      columns={columns}
      data={data}
      enableTopToolbar={false}
      enableColumnActions={false}
      enableSorting
      localization={{
        noRecordsToDisplay: "No records found",
      }}
      muiTableHeadCellProps={mergedHeadCellProps}
      muiTableBodyCellProps={mergedBodyCellProps}
      muiTableBodyRowProps={mergedBodyRowProps}
      {...mrtRest}
      initialState={mergedInitialState}
    />
  );

  return (
    <>
      {heading ? (
        <MDTypography fontWeight="light" fontSize="17px" color="#263445" sx={{ mb: 2 }}>
          {heading}
        </MDTypography>
      ) : null}
      <ThemeProvider theme={resolvedTheme}>
        {disableCard ? table : <Card sx={{ mb: 3, ...(cardSx || {}) }}>{table}</Card>}
      </ThemeProvider>
    </>
  );
}

INDataTable.propTypes = {
  sectionTitle: PropTypes.string,
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  cardSx: PropTypes.object,
  initialState: PropTypes.object,
  tableTheme: PropTypes.object,
  disableCard: PropTypes.bool,
};

INDataTable.defaultProps = {
  sectionTitle: undefined,
  title: undefined,
  cardSx: undefined,
  initialState: undefined,
  tableTheme: undefined,
  disableCard: false,
};

export default INDataTable;
